import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Clock, DollarSign } from "lucide-react";
import { toast } from "sonner";
import { useAuthStore, usePermissions } from "@/stores";
import { useCurrentShift, useOpenShift } from "@/features/shifts/api";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from "@/components/ui";

const EXEMPT_PATHS = ["/login", "/kitchen", "/shifts"];

export function ShiftStartPrompt() {
  const location = useLocation();
  const navigate = useNavigate();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const { hasPermission } = usePermissions();
  const { data: currentShift, isLoading } = useCurrentShift();
  const openShift = useOpenShift();
  const [amount, setAmount] = useState("");

  const exempt = EXEMPT_PATHS.some((p) => location.pathname.startsWith(p));
  const open =
    isAuthenticated && !exempt && !isLoading && !currentShift;

  const canOpenShift = hasPermission("shifts.manage");

  const handleOpen = async (e: React.FormEvent) => {
    e.preventDefault();
    const value = parseFloat(amount);
    if (Number.isNaN(value) || value < 0) {
      toast.error("Enter a valid opening cash amount");

      return;
    }
    try {
      await openShift.mutateAsync({ openingCash: Math.round(value * 100) });
      toast.success("Shift opened");
      setAmount("");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to open shift");
    }
  };

  if (!open) return null;

  return (
    <Dialog open modal onOpenChange={() => {}}>
      <DialogContent
        className="sm:max-w-[420px] [&>button[type=button]]:hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
            <Clock className="h-6 w-6 text-primary" />
          </div>
          <DialogTitle className="text-center">Start a shift</DialogTitle>
          <DialogDescription className="text-center">
            No shift is open right now. Enter the cash in the drawer to begin
            taking orders.
          </DialogDescription>
        </DialogHeader>
        {canOpenShift ? (
          <form onSubmit={handleOpen} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="opening-cash">Opening cash</Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="opening-cash"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="e.g., 100.00"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="pl-9"
                  autoFocus
                  required
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="submit" disabled={openShift.isPending}>
                {openShift.isPending ? "Opening..." : "Open shift"}
              </Button>
            </DialogFooter>
          </form>
        ) : (
          <div className="space-y-4">
            <p className="rounded-md border bg-muted/40 p-3 text-sm text-muted-foreground">
              You don't have permission to open a shift. Ask a manager to start
              one before continuing.
            </p>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => navigate("/shifts")}
              >
                Go to shifts
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
